import {
  GeniusData,
  GeniusPlayerCheckFields,
  GeniusTeamCheckFields,
  GeniusFixtureRoster
} from './types'
import { metricsServerApi } from '../../modules/servers/api'
import {
  setGeniusStatus,
  updateFixtureFromAPIResponse,
  updatePlayersFromAPIResponse,
  addFetchingPlayerIds,
  removeFetchingPlayerIds,
  setRoster
} from './slice'

// Request/response types

export type GeniusStatusResponseBody = GeniusData

export type FixtureCheckRequestBody = {
  homeTeam: GeniusTeamCheckFields
  awayTeam: GeniusTeamCheckFields
  dataTime: string
}

export type FixtureCheckResponseBody = GeniusData

export type PlayerCheckRequestBody = GeniusPlayerCheckFields

export type PlayersCheckResponseBody = GeniusData['playerIds']

export type PlayersCheckRequestBody = GeniusPlayerCheckFields[]

export type GetGeniusFixtureTeamRosterResponseBody = GeniusFixtureRoster

// RTK Query endpoints

export const geniusApi = metricsServerApi.injectEndpoints({
  endpoints: (builder) => ({
    checkGeniusStatus: builder.query<GeniusStatusResponseBody, void>({
      query: () => ({
        url: 'api/genius/status',
        method: 'GET'
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setGeniusStatus(data))
          dispatch(metricsServerApi.util.invalidateTags(['geniusRoster']))
        } catch (error) {
          console.error('Error checking genius status:', error)
        }
      }
    }),

    checkFixture: builder.mutation<
      FixtureCheckResponseBody,
      FixtureCheckRequestBody
    >({
      query: (data) => ({
        url: '/api/genius/resolve_fixture ',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['geniusRoster'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(updateFixtureFromAPIResponse(data))
        } catch (error) {
          console.error('Error checking fixture:', error)
        }
      }
    }),

    checkPlayer: builder.mutation<
      PlayersCheckResponseBody,
      PlayerCheckRequestBody
    >({
      query: (data) => ({
        url: 'api/genius/resolve_player',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['geniusRoster'],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        dispatch(addFetchingPlayerIds([data.playerId]))
        try {
          const { data: responseData } = await queryFulfilled
          dispatch(updatePlayersFromAPIResponse(responseData))
          dispatch(removeFetchingPlayerIds([data.playerId]))
        } catch (error) {
          console.error('Error checking player:', error)
        }
      }
    }),

    checkPlayers: builder.mutation<
      PlayersCheckResponseBody,
      PlayersCheckRequestBody
    >({
      query: (data) => ({
        url: 'api/genius/resolve_players',
        method: 'POST',
        body: data
      }),
      invalidatesTags: ['geniusRoster'],
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const playerIds = data.map((p) => p.playerId)
        dispatch(addFetchingPlayerIds(playerIds))
        try {
          const { data: responseData } = await queryFulfilled
          dispatch(updatePlayersFromAPIResponse(responseData))
          dispatch(removeFetchingPlayerIds(playerIds))
        } catch (error) {
          console.error('Error checking players:', error)
        }
      }
    }),

    getGeniusFixtureRoster: builder.query<
      GetGeniusFixtureTeamRosterResponseBody,
      void
    >({
      query: () => ({
        url: 'api/genius/roster',
        method: 'GET'
      }),
      providesTags: ['geniusRoster'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(setRoster(data))
        } catch (error) {
          console.error('Error getting genius roster:', error)
        }
      }
    })
  })
})

export const {
  useCheckGeniusStatusQuery,
  useLazyCheckGeniusStatusQuery,
  useCheckFixtureMutation,
  useCheckPlayerMutation,
  useCheckPlayersMutation,
  useGetGeniusFixtureRosterQuery,
  useLazyGetGeniusFixtureRosterQuery
} = geniusApi
